import React, { useState, useMemo } from "react";
import ChartContainer from "./ChartContainer";
import DataTable from "./DataTable";

const HeadcountDetails = ({ data, summary, dateRange }) => {
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));

  const analysis = summary.headcountAnalysis || {};
  
  // Define colors for charts
  const colors = [
    "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", 
    "#06B6D4", "#84CC16", "#F97316", "#EC4899", "#6B7280"
  ];
  
  // Helper function to get latest month with data
  const getLatestMonthWithData = (monthlyData) => {
    if (!monthlyData) return 'N/A';
    
    const monthOrder = ['Dec', 'Nov', 'Oct', 'Sep', 'Aug', 'Jul', 'Jun', 'May', 'Apr', 'Mar', 'Feb', 'Jan'];
    
    for (const month of monthOrder) {
      if (monthlyData[month] && monthlyData[month] > 0) {
        return month;
      }
    }
    return 'N/A';
  };

  // Helper function to format monthly breakdown
  const formatMonthlyBreakdown = (monthlyData) => {
    if (!monthlyData) return 'N/A';
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const validMonths = months.filter(month => monthlyData[month] && monthlyData[month] > 0);
    
    if (validMonths.length === 0) return 'No data';
    
    // Show latest 3 months with data
    return validMonths.slice(-3).map(month => `${month}: ${monthlyData[month]}`).join(', ');
  };

  // Get available divisions and years
  const divisions = useMemo(() => {
    const divisionSet = new Set();
    data.forEach(record => {
      if (record.division) {
        divisionSet.add(record.division);
      }
    });
    return Array.from(divisionSet).sort();
  }, [data]);

  // Fixed range: 2024 through the current year (newest first), independent of
  // which years actually have data — a year with no records simply shows empty.
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const list = [];
    for (let y = currentYear; y >= 2024; y--) {
      list.push(String(y));
    }
    return list;
  }, []);

  // Calculate filtered statistics
  const filteredStats = useMemo(() => {
    if (!analysis.divisionStats) return {};

    let stats = analysis.divisionStats;
    
    if (selectedDivision !== "All") {
      stats = stats.filter(div => div.division === selectedDivision);
    }

    // Calculate totals for selected filters
    let totalHeadcount = 0;
    let totalJoiners = 0;
    let totalResignees = 0;
    let avgAttritionRate = 0;
    let divisionCount = 0;

    const targetYear = selectedYear !== "All" ? selectedYear : years[0];
    
    stats.forEach(div => {
      if (div.years[targetYear]) {
        totalHeadcount += div.years[targetYear].headcount;
        totalJoiners += div.years[targetYear].joiners;
        totalResignees += div.years[targetYear].resignees;
        avgAttritionRate += div.years[targetYear].attritionRate;
        divisionCount++;
      }
    });

    avgAttritionRate = divisionCount > 0 ? avgAttritionRate / divisionCount : 0;

    return {
      totalHeadcount,
      totalJoiners,
      totalResignees,
      avgAttritionRate: Math.round(avgAttritionRate * 100) / 100,
      divisionCount,
      targetYear
    };
  }, [analysis, selectedDivision, selectedYear, years]);

  // Multi-division chart data (for stacked bars and multi-line charts)
  const multiDivisionData = useMemo(() => {
    if (!analysis.divisionStats) return {};

    const targetYear = selectedYear !== "All" ? selectedYear : years[0];
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get all divisions with data for the target year
    const divisionsWithData = analysis.divisionStats.filter(div => 
      div.years && div.years[targetYear]
    );

    if (divisionsWithData.length === 0) return {};

    // Find the last month with any data across all divisions
    let lastMonthIndex = -1;
    divisionsWithData.forEach(div => {
      const monthlyHeadcount = div.years[targetYear].monthlyHeadcount || {};
      monthOrder.forEach((month, index) => {
        if (monthlyHeadcount[month] && monthlyHeadcount[month] > 0) {
          lastMonthIndex = Math.max(lastMonthIndex, index);
        }
      });
    });

    if (lastMonthIndex === -1) return {};

    // Get months up to the last month with data
    const relevantMonths = monthOrder.slice(0, lastMonthIndex + 1);
    
    // Prepare data for each chart type
    const stackedHeadcount = {
      labels: relevantMonths,
      datasets: divisionsWithData.map((div, index) => ({
        label: div.division,
        data: relevantMonths.map(month => 
          div.years[targetYear].monthlyHeadcount?.[month] || 0
        ),
        backgroundColor: colors[index % colors.length] + '80',
        borderColor: colors[index % colors.length],
        borderWidth: 1
      }))
    };

    const stackedJoiners = {
      labels: relevantMonths,
      datasets: divisionsWithData.map((div, index) => ({
        label: div.division,
        data: relevantMonths.map(month => 
          div.years[targetYear].monthlyJoiners?.[month] || 0
        ),
        backgroundColor: colors[index % colors.length] + '80',
        borderColor: colors[index % colors.length],
        borderWidth: 1
      }))
    };

    const stackedResignees = {
      labels: relevantMonths,
      datasets: divisionsWithData.map((div, index) => ({
        label: div.division,
        data: relevantMonths.map(month => 
          div.years[targetYear].monthlyResignees?.[month] || 0
        ),
        backgroundColor: colors[index % colors.length] + '80',
        borderColor: colors[index % colors.length],
        borderWidth: 1
      }))
    };

    // Multi-line data (same data, different visualization)
    const multiLineHeadcount = {
      labels: relevantMonths,
      datasets: divisionsWithData.map((div, index) => ({
        label: div.division,
        data: relevantMonths.map(month => 
          div.years[targetYear].monthlyHeadcount?.[month] || 0
        ),
        borderColor: colors[index % colors.length],
        backgroundColor: 'transparent',
        tension: 0.4,
        fill: false
      }))
    };

    return {
      stackedHeadcount,
      stackedJoiners,
      stackedResignees,
      multiLineHeadcount,
      relevantMonths,
      targetYear
    };
  }, [analysis, selectedYear, years, colors]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!analysis.divisionStats) return {};

    const targetYear = selectedYear !== "All" ? selectedYear : years[0];
    
    // Division headcount breakdown
    const divisionHeadcount = {};
    const divisionAttrition = {};
    
    analysis.divisionStats.forEach(div => {
      if (div.years[targetYear]) {
        if (selectedDivision === "All" || div.division === selectedDivision) {
          divisionHeadcount[div.division] = div.years[targetYear].headcount;
          divisionAttrition[div.division] = div.years[targetYear].attritionRate;
        }
      }
    });

    // Helper function to get months in chronological order with data
    const getOrderedMonthsWithData = (monthlyData) => {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const orderedData = {};
      let lastMonthWithData = -1;
      
      // Find the last month with any data
      monthOrder.forEach((month, index) => {
        if (monthlyData[month] !== undefined && monthlyData[month] > 0) {
          lastMonthWithData = index;
        }
      });
      
      // Only include months up to the last month with data
      if (lastMonthWithData >= 0) {
        for (let i = 0; i <= lastMonthWithData; i++) {
          const month = monthOrder[i];
          orderedData[month] = monthlyData[month] || 0;
        }
      }
      
      return orderedData;
    };

    // Monthly trends for selected division
    let monthlyTrends = {};
    if (selectedDivision !== "All" && analysis.monthlyTrends?.[selectedDivision]?.[targetYear]) {
      const divTrends = analysis.monthlyTrends[selectedDivision][targetYear];
      monthlyTrends = {
        headcount: getOrderedMonthsWithData(divTrends.headcount),
        joiners: getOrderedMonthsWithData(divTrends.joiners),
        resignees: getOrderedMonthsWithData(divTrends.resignees)
      };
    } else if (selectedDivision === "All") {
      // Aggregate monthly trends for all divisions
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const aggregated = { headcount: {}, joiners: {}, resignees: {} };
      
      months.forEach(month => {
        aggregated.headcount[month] = 0;
        aggregated.joiners[month] = 0;
        aggregated.resignees[month] = 0;
      });

      Object.keys(analysis.monthlyTrends || {}).forEach(division => {
        if (analysis.monthlyTrends[division][targetYear]) {
          const divTrends = analysis.monthlyTrends[division][targetYear];
          months.forEach(month => {
            if (divTrends.headcount[month] !== undefined) {
              aggregated.headcount[month] += divTrends.headcount[month];
            }
            if (divTrends.joiners[month] !== undefined) {
              aggregated.joiners[month] += divTrends.joiners[month];
            }
            if (divTrends.resignees[month] !== undefined) {
              aggregated.resignees[month] += divTrends.resignees[month];
            }
          });
        }
      });
      
      // Apply chronological ordering to aggregated data
      monthlyTrends = {
        headcount: getOrderedMonthsWithData(aggregated.headcount),
        joiners: getOrderedMonthsWithData(aggregated.joiners),
        resignees: getOrderedMonthsWithData(aggregated.resignees)
      };
    }

    return {
      divisionHeadcount,
      divisionAttrition,
      monthlyTrends,
      targetYear
    };
  }, [analysis, selectedDivision, selectedYear, years]);

  // Prepare processed table data (one row per division)
  const processedTableData = useMemo(() => {
    if (!analysis.divisionStats) {
      return [];
    }

    const targetYear = selectedYear !== "All" ? selectedYear : years[0];
    const tableData = [];

    analysis.divisionStats.forEach((div, index) => {
      if (div.years && div.years[targetYear]) {
        const divisionData = div.years[targetYear];
        
        // Only include if matches filter
        if (selectedDivision === "All" || div.division === selectedDivision) {
          // Add current headcount status
          const latestMonth = getLatestMonthWithData(divisionData.monthlyHeadcount);
          
          const tableRow = {
            division: div.division,
            currentHeadcount: divisionData.headcount,
            joiners: divisionData.joiners,
            resignees: divisionData.resignees,
            attritionRate: divisionData.attritionRate,
            year: targetYear,
            latestMonth: latestMonth,
            // Monthly breakdown for the latest few months
            monthlyBreakdown: formatMonthlyBreakdown(divisionData.monthlyHeadcount)
          };
          
          tableData.push(tableRow);
        }
      }
    });

    return tableData.sort((a, b) => a.division.localeCompare(b.division));
  }, [analysis, selectedDivision, selectedYear, years, getLatestMonthWithData, formatMonthlyBreakdown]);

  // Empty state check
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Headcount Data</h3>
        <p className="text-sm text-gray-600 text-center max-w-md">
          No data for the selected period.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Division:</label>
          <select
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="All">All Divisions ({divisions.length})</option>
            {divisions.map((division) => (
              <option key={division} value={division}>
                {division}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="All">Latest ({years[0]})</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Headcount</p>
              <p className="text-2xl font-bold text-blue-900">
                {filteredStats.totalHeadcount || 0}
              </p>
              <p className="text-xs text-blue-500">
                {filteredStats.targetYear} - {selectedDivision}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Joiners</p>
              <p className="text-2xl font-bold text-green-900">
                {filteredStats.totalJoiners || 0}
              </p>
              <p className="text-xs text-green-500">New hires</p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600">Resignees</p>
              <p className="text-2xl font-bold text-red-900">
                {filteredStats.totalResignees || 0}
              </p>
              <p className="text-xs text-red-500">Departures</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Attrition Rate</p>
              <p className="text-2xl font-bold text-orange-900">
                {filteredStats.avgAttritionRate || 0}%
              </p>
              <p className="text-xs text-orange-500">Annual average</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1: Division Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartContainer
          title={`Headcount by Division (${chartData.targetYear})`}
          data={chartData.divisionHeadcount || {}}
          type="bar"
          height={300}
        />
        <ChartContainer
          title={`Attrition Rate by Division (${chartData.targetYear})`}
          data={chartData.divisionAttrition || {}}
          type="bar"
          height={300}
          options={{
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Attrition Rate (%)'
                }
              }
            }
          }}
        />
      </div>

      {/* Charts Row 2: Monthly Trends */}
      {chartData.monthlyTrends && Object.keys(chartData.monthlyTrends.headcount || {}).length > 0 && (
        <div className="grid grid-cols-1 gap-6">
          <ChartContainer
            title={`Monthly Headcount Trends (${chartData.targetYear})${
              selectedDivision !== "All" ? ` - ${selectedDivision}` : " - All Divisions"
            }`}
            data={chartData.monthlyTrends.headcount || {}}
            type="line"
            height={300}
            options={{
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Employees'
                  }
                }
              }
            }}
          />
        </div>
      )}

      {/* Charts Row 3: Joiners vs Resignees */}
      {chartData.monthlyTrends && Object.keys(chartData.monthlyTrends.joiners || {}).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartContainer
            title={`Monthly Joiners (${chartData.targetYear})`}
            data={chartData.monthlyTrends.joiners || {}}
            type="bar"
            height={300}
            preserveOrder={true}
            backgroundColor="rgba(34, 197, 94, 0.6)"
            borderColor="rgba(34, 197, 94, 1)"
          />
          <ChartContainer
            title={`Monthly Resignees (${chartData.targetYear})`}
            data={chartData.monthlyTrends.resignees || {}}
            type="bar"
            height={300}
            preserveOrder={true}
            backgroundColor="rgba(239, 68, 68, 0.6)"
            borderColor="rgba(239, 68, 68, 1)"
          />
        </div>
      )}

      {/* Charts Row 4: Multi-Division Analysis */}
      {multiDivisionData.stackedHeadcount && multiDivisionData.stackedHeadcount.datasets.length > 0 && (
        <div className="space-y-6">
          <h4 className="text-lg font-medium text-gray-900">Multi-division Analysis</h4>
          
          {/* Stacked Charts */}
          <div className="grid grid-cols-1 gap-6">
            <ChartContainer
              title={`Headcount by Division - Multi-Line View (${multiDivisionData.targetYear})`}
              data={multiDivisionData.multiLineHeadcount}
              type="line"
              height={400}
              multiDataset={true}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartContainer
              title={`Headcount by Division - Stacked (${multiDivisionData.targetYear})`}
              data={multiDivisionData.stackedHeadcount}
              type="bar"
              height={350}
              multiDataset={true}
              stacked={true}
            />
            <ChartContainer
              title={`Joiners by Division - Stacked (${multiDivisionData.targetYear})`}
              data={multiDivisionData.stackedJoiners}
              type="bar"
              height={350}
              multiDataset={true}
              stacked={true}
            />
            <ChartContainer
              title={`Resignees by Division - Stacked (${multiDivisionData.targetYear})`}
              data={multiDivisionData.stackedResignees}
              type="bar"
              height={350}
              multiDataset={true}
              stacked={true}
            />
          </div>
        </div>
      )}

      {/* Detailed Data Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Detailed Headcount Data
            {selectedDivision !== "All" && ` - ${selectedDivision}`}
            {selectedYear !== "All" && ` (${selectedYear})`}
          </h3>
          
          {/* Simple table for headcount data */}
          <div className="overflow-x-auto">
            <DataTable
              data={processedTableData}
              category="headcount"
            />
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default HeadcountDetails; 